/**
 * GameClient - Unified interface for local and remote game servers
 *
 * This provides a single interface that can switch between:
 * - Remote server (Colyseus over WebSocket)
 * - Local server (embedded, offline play)
 */

import { PlayerState, TileType, ChunkData, SlashData, PortalAddress, RodentType, findPortalSpawnPosition, findStandingPosition, WorldData, globalPortalRegistry } from '@blockworld/shared';

import { NetworkClient, PlayerSnapshot, ConnectionStatus } from './NetworkClient';
import { LocalGameServer } from './LocalGameServer';

export type GameMode = 'online' | 'offline';

export class GameClient {
    private networkClient: NetworkClient | null = null;
    private localServer: LocalGameServer | null = null;
    private mode: GameMode = 'offline';

    /** Persistent device identity for retention tracking */
    deviceId: string = '';

    // Saved local world state (preserved when teleporting to server)
    private savedLocalSeed: number | null = null;
    private savedLocalWorldData: any = null;  // WorldData serialized

    /** Return the saved local world seed (used to calculate correct spawn Y after online death). */
    getLocalSeed(): number | null {
        return this.savedLocalSeed;
    }

    // Current player state (tracked from sendPosition calls)
    private currentPlayerState: {
        isHoldingBlock: boolean;
        heldBlockType: number;
        facing: number;
        jumpHeight: number;
        hasBoots: boolean;
    } = { isHoldingBlock: false, heldBlockType: -1, facing: -1, jumpHeight: 0, hasBoots: false };

    // Portal cooldown to prevent immediate re-triggering
    private portalCooldownUntil: number = 0;

    // Teleport mutual exclusion — prevents concurrent teleportTo() calls
    // from creating multiple orphaned server sessions
    private teleportInProgress: boolean = false;

    // Lightweight WorldData for online mode (tracks kiwen metadata like signs, pressure plates, etc.)
    private onlineWorldData: WorldData | null = null;

    // Pre-checked server status (checked in background at startup)
    private serverReachabilityCache: Map<string, { reachable: boolean; checkedAt: number }> = new Map();
    private readonly CACHE_TTL = 30000; // 30 second cache

    // Callbacks (same interface as NetworkClient)
    onConnectionChange: ((status: ConnectionStatus) => void) | null = null;
    onPlayerCountChange: ((count: number) => void) | null = null;
    onChunkData: ((chunks: ChunkData[]) => void) | null = null;
    onBlockUpdate: ((updates: {
        x: number;
        y: number;
        tileType: number;
        action: string;
        inertiaDir?: number;
        playerId?: string;
        playerX?: number;
        playerY?: number;
        playerState?: PlayerState;
        playerFacing?: number;
        playerIsCrouching?: boolean;
        playerIsHoldingBlock?: boolean;
        playerHeldBlockType?: number;
    }[]) => void) | null = null;
    onBlockActionRollback: ((data: { action: string; x: number; y: number; blockType?: number; reason: string }) => void) | null = null;
    onPlayerPositions: ((snapshots: PlayerSnapshot[]) => void) | null = null;
    onPlayerJoin: ((player: PlayerSnapshot) => void) | null = null;
    onPlayerLeave: ((playerId: string) => void) | null = null;
    onInitialState: ((localId: string, seed: number, players: PlayerSnapshot[]) => void) | null = null;
    onAirStrafe: ((playerId: string, dir: number) => void) | null = null;
    onSlash: ((slash: SlashData) => void) | null = null;
    onDamage: ((playerId: string, hp: number, source: 'sword' | 'slash' | 'mouse' | 'drowning') => void) | null = null;
    onMaxHpUpdate: ((maxHp: number, hp: number) => void) | null = null;
    onDeath: ((playerId: string) => void) | null = null;
    onRuneUpdate: ((x: number, y: number, hieroglyphIndex: number) => void) | null = null;
    onTeleport: ((destination: PortalAddress, portalCode: number[]) => void) | null = null;
    onWorldReset: ((seed: number) => void) | null = null;
    onSpeech: ((playerId: string, words: number[], voice: string) => void) | null = null;

    // Mouse/rodent enemy callbacks
    onMouseSpawn: ((id: number, x: number, y: number, facing: number, rodentType: string, wallDir?: number, vertDir?: number) => void) | null = null;
    onMouseDeath: ((id: number) => void) | null = null;
    onMouseDespawn: ((id: number) => void) | null = null;

    // Block spawn callback
    onBlockSpawn: ((blockType: TileType) => void) | null = null;

    // Jump height update callback
    onJumpHeightUpdate: ((jumpHeight: number) => void) | null = null;

    // Yellow key pickup callback
    onYellowKeyPickup: (() => void) | null = null;

    // Air update callback (server-authoritative breathing state)
    onAirUpdate: ((airTicks: number) => void) | null = null;

    // Pressure plate state change
    onPressurePlateChanged: ((x: number, y: number, active: boolean) => void) | null = null;

    // Sign glyph update
    onSignUpdate: ((x: number, y: number, glyphIndex: number) => void) | null = null;

    // Eye switch activation
    onEyeSwitchActivated: ((x: number, y: number, tick: number) => void) | null = null;

    // Spawn point set confirmation
    onSpawnPointSet: ((x: number, y: number) => void) | null = null;

    // Custom respawn (KIWEN_MI)
    onRespawnAt: ((x: number, y: number) => void) | null = null;

    // Server load indicator (0=healthy, 1=degraded, 2=critical)
    onServerLoad: ((level: number) => void) | null = null;

    constructor() {}

    /**
     * Connect to a remote server
     */
    async connectOnline(serverUrl: string, options?: { isHoldingBlock?: boolean; heldBlockType?: number; facing?: number }): Promise<void> {
        this.mode = 'online';
        this.localServer = null;

        this.networkClient = new NetworkClient(serverUrl);
        this.setupNetworkCallbacks();

        // Include device ID for retention tracking
        const connectOptions = { ...options, deviceId: this.deviceId || undefined };
        await this.networkClient.connect(connectOptions);
    }

    /**
     * Start offline/local mode
     */
    async connectOffline(seed?: number): Promise<void> {
        console.log('[GAME] connectOffline called');
        this.mode = 'offline';
        this.networkClient = null;

        this.localServer = new LocalGameServer(seed);
        console.log('[GAME] LocalGameServer created, setting up callbacks');
        this.setupLocalCallbacks();
        console.log('[GAME] Callbacks set up, calling connect()');

        await this.localServer.connect();
        console.log('[GAME] LocalServer connected');

        // Start background check of default portal server
        this.preCheckDefaultPortalServer();
    }

    /**
     * Pre-check the default portal server in the background
     * This runs at startup so teleport is instant when player reaches portal
     */
    private preCheckDefaultPortalServer(): void {
        const defaultServerUrl = globalPortalRegistry.getDefault().serverUrl || 'wss://blockworld-server-v2.fly.dev';
        console.log(`[PORTAL] Pre-checking server connectivity: ${defaultServerUrl}`);

        this.checkServerReachable(defaultServerUrl).then((reachable) => {
            this.serverReachabilityCache.set(defaultServerUrl, {
                reachable,
                checkedAt: Date.now(),
            });

            if (reachable) {
                console.log(`[PORTAL] Server is ONLINE - portal [0] will teleport instantly`);
            } else {
                console.log(`[PORTAL] Server is OFFLINE - portal [0] disabled`);
            }
        });
    }

    /**
     * Disconnect from current server.
     * Async because NetworkClient.disconnect() awaits room.leave() with a
     * timeout to ensure the old WebSocket close frame is sent before a new
     * connection is established.
     */
    async disconnect(): Promise<void> {
        if (this.networkClient) {
            await this.networkClient.disconnect();
            this.networkClient = null;
        }
        if (this.localServer) {
            this.localServer.disconnect();
            this.localServer = null;
        }
        this.onlineWorldData = null;
    }

    /**
     * Get current game mode
     */
    getMode(): GameMode {
        return this.mode;
    }

    /**
     * Check if connected
     */
    isConnected(): boolean {
        return this.networkClient !== null || this.localServer !== null;
    }

    /**
     * Get world data (for kiwen block metadata — signs, pressure plates, eye switches, etc.)
     * In offline mode, returns the LocalGameServer's worldData.
     * In online mode, returns a lightweight WorldData for client-side metadata tracking.
     */
    get worldData(): WorldData | undefined {
        if (this.localServer) {
            return this.localServer.worldData;
        }
        if (this.networkClient) {
            if (!this.onlineWorldData) {
                this.onlineWorldData = new WorldData(0);
            }
            return this.onlineWorldData;
        }
        return undefined;
    }

    // ============================================================
    // GAME ACTIONS (forwarded to active server)
    // ============================================================

    sendPosition(
        x: number,
        y: number,
        state: PlayerState,
        facing: number,
        isCrouching: boolean,
        isHoldingBlock: boolean,
        heldBlockType: TileType | null
    ): void {
        // Track player state for teleport preservation
        this.currentPlayerState = {
            ...this.currentPlayerState,
            isHoldingBlock,
            heldBlockType: heldBlockType ?? -1,
            facing,
        };

        if (this.networkClient) {
            this.networkClient.sendPosition(x, y, state, facing, isCrouching, isHoldingBlock, heldBlockType);
        } else if (this.localServer) {
            this.localServer.sendPosition(x, y, state, facing, isCrouching, isHoldingBlock, heldBlockType);
        }
    }

    sendBlockAction(
        action: 'pickup' | 'place' | 'combine',
        x: number,
        y: number,
        blockType: TileType,
        playerX: number,
        playerY: number,
        playerState: PlayerState,
        playerFacing: number,
        playerIsCrouching: boolean,
        playerIsHoldingBlock: boolean,
        playerHeldBlockType: number
    ): void {
        if (this.networkClient) {
            this.networkClient.sendBlockAction(
                action, x, y, blockType,
                playerX, playerY, playerState, playerFacing,
                playerIsCrouching, playerIsHoldingBlock, playerHeldBlockType
            );
        } else if (this.localServer) {
            this.localServer.sendBlockAction(
                action, x, y, blockType,
                playerX, playerY, playerState, playerFacing,
                playerIsCrouching, playerIsHoldingBlock, playerHeldBlockType
            );
        }
    }

    sendSwordHold(holding: boolean): void {
        if (this.networkClient) {
            this.networkClient.sendSwordHold(holding);
        } else if (this.localServer) {
            this.localServer.sendSwordHold(holding);
        }
    }

    sendToolAction(action: string, playerX: number, playerY: number, facing: number, isCrouching: boolean): void {
        if (this.networkClient) {
            this.networkClient.sendToolAction(action, playerX, playerY, facing, isCrouching);
        } else if (this.localServer) {
            this.localServer.sendToolAction(action, playerX, playerY, facing, isCrouching);
        }
    }

    sendAirStrafe(dir: number): void {
        if (this.networkClient) {
            this.networkClient.sendAirStrafe(dir);
        } else if (this.localServer) {
            this.localServer.sendAirStrafe(dir);
        }
    }

    subscribeToChunks(chunks: { cx: number; cy: number }[]): void {
        if (this.networkClient) {
            this.networkClient.subscribeToChunks(chunks);
        } else if (this.localServer) {
            this.localServer.subscribeToChunks(chunks);
        }
    }

    /**
     * Request server to reset world (deletes save, creates fresh world with new seed)
     * Only works when connected to online server
     */
    sendResetWorld(): void {
        if (this.networkClient) {
            this.networkClient.sendResetWorld();
        }
    }

    /**
     * Send speech message (toki pona glyph indices)
     */
    sendPortalActivate(): void {
        if (this.networkClient) {
            this.networkClient.sendPortalActivate();
        } else if (this.localServer) {
            this.localServer.sendPortalActivate();
        }
    }

    sendSpeech(words: number[], voice: string = 'pona'): void {
        if (this.networkClient) {
            this.networkClient.sendSpeech(words, voice);
        } else if (this.localServer) {
            this.localServer.sendSpeech(words, voice);
        }
    }

    sendSignCycle(x: number, y: number): void {
        if (this.networkClient) {
            this.networkClient.sendSignCycle(x, y);
        } else if (this.localServer) {
            this.localServer.sendSignCycle(x, y);
        }
    }

    sendSetSpawnPoint(x: number, y: number): void {
        if (this.networkClient) {
            this.networkClient.sendSetSpawnPoint(x, y);
        } else if (this.localServer) {
            this.localServer.sendSetSpawnPoint(x, y);
        }
    }

    // ============================================================
    // TELEPORTATION
    // ============================================================

    /**
     * Check if portal teleport is on cooldown
     */
    isOnPortalCooldown(): boolean {
        return Date.now() < this.portalCooldownUntil;
    }

    /**
     * Get cached server reachability or check fresh if cache expired
     */
    private async getServerReachability(serverUrl: string): Promise<boolean> {
        const cached = this.serverReachabilityCache.get(serverUrl);
        const now = Date.now();

        // Use cache if fresh
        if (cached && (now - cached.checkedAt) < this.CACHE_TTL) {
            console.log(`[TELEPORT] Using cached reachability for ${serverUrl}: ${cached.reachable ? 'ONLINE' : 'OFFLINE'}`);
            return cached.reachable;
        }

        // Cache expired or doesn't exist, do fresh check
        console.log(`[TELEPORT] Cache miss/expired, checking server: ${serverUrl}`);
        const reachable = await this.checkServerReachable(serverUrl);

        this.serverReachabilityCache.set(serverUrl, { reachable, checkedAt: now });
        return reachable;
    }

    /**
     * Teleport to a portal destination (switches between online/offline modes)
     * @param clearStateCallback - Optional callback to clear game state (only called when teleport proceeds)
     */
    async teleportTo(destination: PortalAddress, clearStateCallback?: () => void): Promise<void> {
        // Prevent concurrent teleportTo() calls. When the player holds UP on a rune,
        // sendPortalActivate fires every frame. Each server tick can generate a new
        // teleport event while the first teleportTo is still awaiting (e.g. reachability
        // check or joinOrCreate). Without this guard, each concurrent call creates a
        // new NetworkClient + joinOrCreate, orphaning earlier connections and leaving
        // duplicate sessions on the server.
        if (this.teleportInProgress) {
            console.log(`[TELEPORT] Already in progress, ignoring duplicate call`);
            return;
        }
        this.teleportInProgress = true;

        try {
            console.log(`[TELEPORT] ===== TELEPORT START =====`);
            console.log(`[TELEPORT] Destination:`, destination);
            console.log(`[TELEPORT] Current mode: ${this.mode}`);

            if (destination.isRemote && destination.serverUrl) {
                // Check cached reachability (pre-checked at startup)
                const isReachable = await this.getServerReachability(destination.serverUrl);
                if (!isReachable) {
                    console.log(`[TELEPORT] ABORTED: Server not reachable. Staying in current mode.`);
                    console.log(`[TELEPORT] ===== TELEPORT END (FAILED) =====`);
                    return;
                }

                // Server is reachable - NOW clear game state since we're proceeding
                console.log(`[TELEPORT] Server reachable, proceeding with teleport...`);
                clearStateCallback?.();

                // Save local world state before teleporting
                if (this.mode === 'offline' && this.localServer) {
                    this.savedLocalSeed = this.localServer.worldData.seed;
                    this.savedLocalWorldData = {
                        chunks: Array.from(this.localServer.worldData.chunks.entries()),
                        runeData: Array.from(this.localServer.worldData.runeData.entries()),
                    };
                    console.log(`[TELEPORT] Saved local world state (seed: ${this.savedLocalSeed})`);
                }

                // Disconnect and connect to remote
                console.log(`[TELEPORT] Disconnecting from current mode...`);
                await this.disconnect();

                try {
                    // Pass player state to server (held block, facing direction, jump height)
                    const joinOptions: { isHoldingBlock?: boolean; heldBlockType?: number; facing?: number; jumpHeight?: number; hasBoots?: boolean } = {
                        facing: this.currentPlayerState.facing,
                    };
                    if (this.currentPlayerState.isHoldingBlock) {
                        joinOptions.isHoldingBlock = true;
                        joinOptions.heldBlockType = this.currentPlayerState.heldBlockType;
                    }
                    if (this.currentPlayerState.jumpHeight > 0) {
                        joinOptions.jumpHeight = this.currentPlayerState.jumpHeight;
                    }
                    if (this.currentPlayerState.hasBoots) {
                        joinOptions.hasBoots = true;
                    }
                    await this.connectOnline(destination.serverUrl, joinOptions);
                    console.log(`[TELEPORT] SUCCESS: Connected to remote server: ${destination.serverUrl}`);
                } catch (error: any) {
                    console.error('[TELEPORT] FAILED to connect to remote server');
                    console.error('[TELEPORT] Error type:', error?.constructor?.name || typeof error);
                    console.error('[TELEPORT] Error message:', error?.message || String(error));
                    console.log(`[TELEPORT] Restoring offline mode...`);
                    // Mark server as unreachable in cache
                    this.serverReachabilityCache.set(destination.serverUrl, {
                        reachable: false,
                        checkedAt: Date.now(),
                    });
                    await this.connectOfflineWithSavedWorld();
                }
            } else {
                // Teleporting back to offline/local mode
                console.log(`[TELEPORT] Teleporting back to offline mode...`);
                clearStateCallback?.();
                await this.disconnect();
                console.log(`[TELEPORT] Connecting to offline mode (restoring saved world)...`);
                await this.connectOfflineWithSavedWorld(destination.spawnX, destination.spawnY);
                console.log(`[TELEPORT] SUCCESS: Restored saved offline world`);
            }
            console.log(`[TELEPORT] ===== TELEPORT END =====`);
        } finally {
            this.teleportInProgress = false;
        }
    }

    /**
     * Check if a remote server is reachable before attempting full connection
     */
    private async checkServerReachable(serverUrl: string): Promise<boolean> {
        try {
            // Convert wss:// to https:// for health check
            const httpUrl = serverUrl.replace('wss://', 'https://').replace('ws://', 'http://');

            console.log(`[TELEPORT] Pinging server: ${httpUrl}`);

            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 3000); // 3 second timeout

            const response = await fetch(httpUrl, {
                method: 'HEAD',
                mode: 'no-cors',  // Avoid CORS issues for health check
                signal: controller.signal,
            });

            clearTimeout(timeout);

            // With no-cors mode, we can't read the response, but if fetch doesn't throw, server responded
            console.log(`[TELEPORT] Server responded (status may be opaque due to no-cors)`);
            return true;
        } catch (error: any) {
            console.error(`[TELEPORT] Server unreachable:`);
            console.error(`  - URL: ${serverUrl}`);

            if (error.name === 'AbortError') {
                console.error(`  - Reason: Connection timed out (3s)`);
            } else if (error.message?.includes('ERR_NAME_NOT_RESOLVED')) {
                console.error(`  - Reason: DNS lookup failed - server hostname doesn't exist`);
            } else if (error.message?.includes('ERR_CONNECTION_REFUSED')) {
                console.error(`  - Reason: Connection refused - server is down`);
            } else if (error.message?.includes('ERR_NETWORK')) {
                console.error(`  - Reason: Network error - check your internet connection`);
            } else {
                console.error(`  - Reason: ${error.message || error}`);
            }

            return false;
        }
    }

    /**
     * Connect to offline mode, restoring saved world if available
     */
    private async connectOfflineWithSavedWorld(spawnX?: number, spawnY?: number): Promise<void> {
        this.mode = 'offline';
        this.networkClient = null;

        // Create local server with saved seed or new seed
        const seed = this.savedLocalSeed ?? Date.now();
        this.localServer = new LocalGameServer(seed);

        // Restore saved world data if available
        if (this.savedLocalWorldData) {
            console.log(`[TELEPORT] Restoring saved chunks and rune data...`);
            for (const [key, tiles] of this.savedLocalWorldData.chunks) {
                this.localServer.worldData.chunks.set(key, tiles);
            }
            for (const [key, index] of this.savedLocalWorldData.runeData) {
                this.localServer.worldData.runeData.set(key, index);
            }
        }

        // Restore player's state from before teleport (held block, facing, jump height)
        this.localServer.setPlayerState(
            this.currentPlayerState.isHoldingBlock,
            this.currentPlayerState.heldBlockType,
            this.currentPlayerState.facing,
            this.currentPlayerState.jumpHeight
        );
        if (this.currentPlayerState.isHoldingBlock) {
            console.log(`[TELEPORT] Restoring held block: type ${this.currentPlayerState.heldBlockType}`);
        }
        console.log(`[TELEPORT] Restoring facing direction: ${this.currentPlayerState.facing}`);
        if (this.currentPlayerState.jumpHeight > 0) {
            console.log(`[TELEPORT] Restoring jump height: ${this.currentPlayerState.jumpHeight}`);
        }

        // When returning from online, spawn at the requested position or portal by default
        if (this.savedLocalWorldData) {
            if (spawnX !== undefined && spawnY !== undefined) {
                // Exact spawn coordinates (death return to cavern or mentor house)
                this.localServer.setPlayerPosition(spawnX, spawnY);
                console.log(`[TELEPORT] Spawning at (${spawnX}, ${spawnY})`);
            } else if (spawnX !== undefined && spawnX !== 0) {
                // Custom spawn X — find standing position on surface
                const spawn = findStandingPosition(this.localServer.worldData, spawnX);
                this.localServer.setPlayerPosition(spawn.x, spawn.y);
                console.log(`[TELEPORT] Spawning at (${spawn.x}, ${spawn.y})`);
            } else {
                // Portal return — spawn at the portal door
                const portalSpawn = findPortalSpawnPosition(this.localServer.worldData);
                this.localServer.setPlayerPosition(portalSpawn.x, portalSpawn.y);
                console.log(`[TELEPORT] Spawning at portal: (${portalSpawn.x}, ${portalSpawn.y})`);
            }
        }

        this.setupLocalCallbacks();
        await this.localServer.connect();
    }

    // ============================================================
    // CALLBACK SETUP
    // ============================================================

    private setupNetworkCallbacks(): void {
        if (!this.networkClient) return;

        this.networkClient.onConnectionChange = (status) => this.onConnectionChange?.(status);
        this.networkClient.onPlayerCountChange = (count) => this.onPlayerCountChange?.(count);
        this.networkClient.onChunkData = (chunks) => this.onChunkData?.(chunks);
        this.networkClient.onBlockUpdate = (updates) => this.onBlockUpdate?.(updates);
        this.networkClient.onBlockActionRollback = (data) => this.onBlockActionRollback?.(data);
        this.networkClient.onPlayerPositions = (snapshots) => this.onPlayerPositions?.(snapshots);
        this.networkClient.onPlayerJoin = (player) => this.onPlayerJoin?.(player);
        this.networkClient.onPlayerLeave = (playerId) => this.onPlayerLeave?.(playerId);
        this.networkClient.onInitialState = (localId, seed, players) => this.onInitialState?.(localId, seed, players);
        this.networkClient.onAirStrafe = (playerId, dir) => this.onAirStrafe?.(playerId, dir);
        this.networkClient.onSlash = (slash) => this.onSlash?.(slash);
        this.networkClient.onDamage = (playerId, hp, source) => this.onDamage?.(playerId, hp, source);
        this.networkClient.onMaxHpUpdate = (maxHp, hp) => this.onMaxHpUpdate?.(maxHp, hp);
        this.networkClient.onDeath = (playerId) => this.onDeath?.(playerId);
        this.networkClient.onRuneUpdate = (x, y, index) => this.onRuneUpdate?.(x, y, index);
        this.networkClient.onTeleport = (dest, code) => this.onTeleport?.(dest, code);
        this.networkClient.onWorldReset = (seed) => this.onWorldReset?.(seed);
        this.networkClient.onSpeech = (playerId, words, voice) => this.onSpeech?.(playerId, words, voice);
        this.networkClient.onMouseSpawn = (id, x, y, facing, rodentType, wallDir, vertDir) => this.onMouseSpawn?.(id, x, y, facing, rodentType, wallDir, vertDir);
        this.networkClient.onMouseDeath = (id) => this.onMouseDeath?.(id);
        this.networkClient.onMouseDespawn = (id) => this.onMouseDespawn?.(id);
        this.networkClient.onBlockSpawn = (blockType) => this.onBlockSpawn?.(blockType);
        this.networkClient.onJumpHeightUpdate = (jumpHeight) => {
            this.currentPlayerState.jumpHeight = jumpHeight;
            this.currentPlayerState.hasBoots = true;
            this.onJumpHeightUpdate?.(jumpHeight);
        };
        this.networkClient.onYellowKeyPickup = () => this.onYellowKeyPickup?.();
        this.networkClient.onAirUpdate = (airTicks) => this.onAirUpdate?.(airTicks);
        this.networkClient.onPressurePlateChanged = (x, y, active) => this.onPressurePlateChanged?.(x, y, active);
        this.networkClient.onSignUpdate = (x, y, glyphIndex) => this.onSignUpdate?.(x, y, glyphIndex);
        this.networkClient.onEyeSwitchActivated = (x, y, tick) => this.onEyeSwitchActivated?.(x, y, tick);
        this.networkClient.onSpawnPointSet = (x, y) => this.onSpawnPointSet?.(x, y);
        this.networkClient.onRespawnAt = (x, y) => this.onRespawnAt?.(x, y);
        this.networkClient.onServerLoad = (level) => this.onServerLoad?.(level);
    }

    private setupLocalCallbacks(): void {
        if (!this.localServer) return;

        this.localServer.onConnectionChange = (status) => this.onConnectionChange?.(status);
        this.localServer.onChunkData = (chunks) => this.onChunkData?.(chunks);
        this.localServer.onBlockUpdate = (updates) => this.onBlockUpdate?.(updates);
        this.localServer.onPlayerPositions = (snapshots) => this.onPlayerPositions?.(snapshots);
        this.localServer.onInitialState = (localId, seed, players) => this.onInitialState?.(localId, seed, players);
        this.localServer.onSlash = (slash) => this.onSlash?.(slash);
        this.localServer.onDamage = (playerId, hp, source) => this.onDamage?.(playerId, hp, source);
        this.localServer.onMaxHpUpdate = (maxHp, hp) => this.onMaxHpUpdate?.(maxHp, hp);
        this.localServer.onDeath = (playerId) => this.onDeath?.(playerId);
        this.localServer.onRuneUpdate = (x, y, index) => this.onRuneUpdate?.(x, y, index);
        this.localServer.onTeleport = (dest, code) => this.onTeleport?.(dest, code);
        this.localServer.onSpeech = (playerId, words, voice) => this.onSpeech?.(playerId, words, voice);
        this.localServer.onMouseSpawn = (id, x, y, facing, rodentType, wallDir, vertDir) => this.onMouseSpawn?.(id, x, y, facing, rodentType, wallDir, vertDir);
        this.localServer.onMouseDeath = (id) => this.onMouseDeath?.(id);
        this.localServer.onBlockSpawn = (blockType) => this.onBlockSpawn?.(blockType);
        this.localServer.onJumpHeightUpdate = (jumpHeight) => {
            this.currentPlayerState.jumpHeight = jumpHeight;
            this.currentPlayerState.hasBoots = true;
            this.onJumpHeightUpdate?.(jumpHeight);
        };
        this.localServer.onYellowKeyPickup = () => this.onYellowKeyPickup?.();
        this.localServer.onAirUpdate = (airTicks) => this.onAirUpdate?.(airTicks);
        this.localServer.onPressurePlateChanged = (x, y, active) => this.onPressurePlateChanged?.(x, y, active);
        this.localServer.onSignUpdate = (x, y, glyphIndex) => this.onSignUpdate?.(x, y, glyphIndex);
        this.localServer.onEyeSwitchActivated = (x, y, tick) => this.onEyeSwitchActivated?.(x, y, tick);
        this.localServer.onSpawnPointSet = (x, y) => this.onSpawnPointSet?.(x, y);
        this.localServer.onRespawnAt = (x, y) => this.onRespawnAt?.(x, y);
    }

    // ============================================================
    // LOCAL SERVER ACCESS (for offline persistence)
    // ============================================================

    /**
     * Get the local server instance (for saving world data)
     */
    getLocalServer(): LocalGameServer | null {
        return this.localServer;
    }
}
